import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../icons/Icon";
import { Carregando } from "../components/Layout";
import { api, fmt, type Edital, type ListaPublicada } from "../lib/api";

type Disponiveis = {
  publicacoes: Array<{ id: number; tipo: string; rotulo: string; publicadaEm: string; totalInscricoes: number }>;
  cargos: Array<{ id: number; codigo: string; nome: string; componentes: Array<{ id: number; nome: string }> }>;
  listas: Array<{ codigo: string; rotulo: string }>;
};

/**
 * Consulta pública das listas de classificação.
 *
 * Mostra nome e pontuação, que o item 8.19 autoriza divulgar, e o ano de
 * nascimento, necessário para que o terceiro critério de desempate seja
 * verificável. Não há CPF: publicidade não é o mesmo que exposição.
 */
export function Listas({ edital }: { edital?: Edital | null }) {
  const [opcoes, setOpcoes] = useState<Disponiveis | null>(null);
  const [publicacaoId, setPublicacaoId] = useState("");
  const [cargoId, setCargoId] = useState("");
  const [componenteId, setComponenteId] = useState("");
  const [lista, setLista] = useState("GERAL");
  const [dados, setDados] = useState<ListaPublicada | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    api.get<Disponiveis>("/api/publico/listas")
      .then((d) => {
        setOpcoes(d);
        if (d.publicacoes.length) setPublicacaoId(String(d.publicacoes[0].id));
        if (d.cargos.length) {
          setCargoId(String(d.cargos[0].id));
          if (d.cargos[0].componentes.length) setComponenteId(String(d.cargos[0].componentes[0].id));
        }
      })
      .catch(() => setOpcoes({ publicacoes: [], cargos: [], listas: [] }));
  }, []);

  useEffect(() => {
    if (!publicacaoId || !cargoId) return;
    setCarregando(true);
    const q = new URLSearchParams({ cargoId, lista });
    if (componenteId) q.set("componenteId", componenteId);
    api.get<ListaPublicada>(`/api/publico/listas/${publicacaoId}?${q}`)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setCarregando(false));
  }, [publicacaoId, cargoId, componenteId, lista]);

  if (!opcoes) return <Carregando texto="Carregando as listas" />;

  const cargo = opcoes.cargos.find((c) => String(c.id) === cargoId);

  function exportarCsv() {
    if (!dados) return;
    const cabecalho = ["Posição", "Nome", "Total", "Qualificação", "Experiência", "Nascimento"];
    const linhas = dados.linhas.map((l) => [
      l.posicao, l.nome, fmt.pontos(l.pontosTotal), fmt.pontos(l.pontosQualificacao),
      fmt.pontos(l.pontosExperiencia), l.dataNascimento,
    ]);
    const csv = [cabecalho, ...linhas]
      .map((linha) => linha.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${dados.listaRotulo} - ${dados.cargo}.csv`.replace(/[/\\]/g, "-");
    a.click();
    URL.revokeObjectURL(url);
  }

  if (opcoes.publicacoes.length === 0) {
    const datas = (edital?.cronograma ?? [])
      .filter((f) => /classifica|resultado final|recurso/i.test(f.rotulo))
      .slice(0, 4);
    return (
      <section className="secao">
        <div className="container listas-vazio">
          <div className="pilha pilha-6 listas-vazio-texto">
            <h1 className="display">Listas de classificação</h1>
            <p className="lead medida">
              Ainda não há lista publicada. Quando a Comissão publicar, você encontra aqui as
              quatro listas por cargo, com posição, nome e pontuação.
            </p>

            {datas.length > 0 && (
              <ol className="listas-datas">
                {datas.map((f) => (
                  <li key={f.codigo} className={f.encerrada ? "feita" : f.aberta ? "agora" : ""}>
                    <span className="listas-datas-quando num">{f.periodoFormatado}</span>
                    <span>{f.rotulo}</span>
                  </li>
                ))}
              </ol>
            )}

            <div className="linha linha-quebra">
              <Link to="/area" className="btn btn-primario">
                Consultar minha inscrição
                <Icon nome="direita" tamanho={16} />
              </Link>
              <Link to="/" className="listas-link">Ver o cronograma completo</Link>
            </div>
          </div>

          <div className="listas-previa" aria-hidden="true">
            <div className="listas-previa-topo">
              <span>Posição</span><span>Nome</span><span>Total</span>
            </div>
            {[78, 64, 71, 58, 66, 52].map((w, i) => (
              <div key={i} className="listas-previa-linha" style={{ opacity: 1 - i * 0.14 }}>
                <span className="num">{String(i + 1).padStart(2, "0")}</span>
                <span className="listas-barra" style={{ width: `${w}%` }} />
                <span className="listas-barra listas-barra-curta" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="secao">
      <div className="container pilha pilha-6">
        <div className="pilha pilha-2">
          <h1 className="display">Listas de classificação</h1>
          <p className="lead medida">
            Quatro listas por cargo e componente curricular. A Classificação Geral é a ordem
            usada para convocação, já com a alternância das reservas de vaga aplicada.
          </p>
        </div>

        <div className="cartao filtros" data-ocultar-impressao>
          <div className="campo">
            <label className="campo-rotulo" htmlFor="pub">Publicação</label>
            <select id="pub" className="selecao" value={publicacaoId}
                    onChange={(e) => setPublicacaoId(e.target.value)}>
              {opcoes.publicacoes.map((p) => (
                <option key={p.id} value={p.id}>{p.rotulo}</option>
              ))}
            </select>
          </div>

          <div className="campo">
            <label className="campo-rotulo" htmlFor="cargo">Cargo</label>
            <select id="cargo" className="selecao" value={cargoId}
                    onChange={(e) => {
                      setCargoId(e.target.value);
                      const c = opcoes.cargos.find((x) => String(x.id) === e.target.value);
                      setComponenteId(c?.componentes.length ? String(c.componentes[0].id) : "");
                    }}>
              {opcoes.cargos.map((c) => (
                <option key={c.id} value={c.id}>{c.codigo}: {c.nome}</option>
              ))}
            </select>
          </div>

          {cargo && cargo.componentes.length > 0 && (
            <div className="campo">
              <label className="campo-rotulo" htmlFor="comp">Componente / função</label>
              <select id="comp" className="selecao" value={componenteId}
                      onChange={(e) => setComponenteId(e.target.value)}>
                {cargo.componentes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="campo campo-segmentos">
            <span className="campo-rotulo" id="rotulo-lista">Lista</span>
            <div className="segmentos" role="group" aria-labelledby="rotulo-lista">
              {opcoes.listas.map((l) => (
                <button key={l.codigo} type="button" className="segmento"
                        aria-pressed={lista === l.codigo}
                        onClick={() => setLista(l.codigo)}>
                  {l.rotulo}
                </button>
              ))}
            </div>
          </div>
        </div>

        {carregando && <Carregando texto="Carregando a lista" />}

        {!carregando && dados && (
          <div className="painel">
            <div className="painel-cabecalho linha linha-entre linha-quebra">
              <div>
                <h2 className="h3">{dados.listaRotulo}</h2>
                <p className="texto-sutil">
                  {dados.cargo}{dados.componente ? `, ${dados.componente}` : ""} ·{" "}
                  {dados.total} candidato(s) · publicada em {fmt.dataHora(dados.publicadaEm)}
                </p>
              </div>
              <div className="linha" style={{ gap: "var(--space-2)" }} data-ocultar-impressao>
                <button type="button" className="btn btn-secundario btn-pequeno" onClick={exportarCsv}>
                  <Icon nome="planilha" tamanho={16} /> Exportar CSV
                </button>
                <button type="button" className="btn btn-secundario btn-pequeno" onClick={() => window.print()}>
                  <Icon nome="arquivo" tamanho={16} /> Imprimir
                </button>
              </div>
            </div>

            {dados.linhas.length === 0 ? (
              <p className="vazio">Nenhum candidato classificado nesta lista.</p>
            ) : (
              <div className="tabela-rolagem">
                <table className="tabela tabela-cartoes tabela-lista">
                  <caption className="so-leitor">
                    {dados.listaRotulo} de {dados.cargo}. Ordenada por pontuação total; empates
                    resolvidos por maior qualificação, maior experiência e maior idade.
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Posição</th>
                      <th scope="col">Nome</th>
                      <th scope="col" className="col-num">Total</th>
                      <th scope="col" className="col-num">Qualificação</th>
                      <th scope="col" className="col-num">Experiência</th>
                      <th scope="col" className="col-num">Nascimento</th>
                      {dados.lista === "GERAL" && <th scope="col">Origem da vaga</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {dados.linhas.map((l) => (
                      <tr key={l.posicao}>
                        <td className="col-posicao" data-rotulo="Posição">{l.posicao}</td>
                        <td className="col-nome" data-rotulo="Nome">{l.nome}</td>
                        <td className="col-num" data-rotulo="Total"><strong>{fmt.pontos(l.pontosTotal)}</strong></td>
                        <td className="col-num" data-rotulo="Qualificação">{fmt.pontos(l.pontosQualificacao)}</td>
                        <td className="col-num" data-rotulo="Experiência">{fmt.pontos(l.pontosExperiencia)}</td>
                        <td className="col-num" data-rotulo="Nascimento">{l.dataNascimento}</td>
                        {dados.lista === "GERAL" && (
                          <td data-rotulo="Origem da vaga">
                            {l.origemCota === "PCD" && (
                              <span className="etiqueta etiqueta-pcd">Reserva PcD</span>
                            )}
                            {l.origemCota === "NEGRO" && (
                              <span className="etiqueta etiqueta-negro">Reserva negros</span>
                            )}
                            {!l.origemCota && l.revertida && (
                              <span className="etiqueta">Revertida à ampla</span>
                            )}
                            {!l.origemCota && !l.revertida && (
                              <span className="texto-sutil">Ampla concorrência</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="painel-corpo" style={{ borderTop: "1px solid var(--color-border)" }}>
              <p className="texto-sutil">
                <Icon nome="informacao" tamanho={14} /> Critérios de desempate: maior pontuação
                de qualificação; depois maior pontuação de experiência; depois maior idade.
                Posições marcadas como reserva foram atribuídas pela alternância das cotas;
                posições revertidas são as reservadas sem candidato inscrito naquela
                modalidade.
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
